// Edit grid modal — subset of fields matching legacy showEditGridModal.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { GridConfig } from "@/types/api";
import { apiPatch } from "@/lib/api-client";
import Modal from "@/components/common/modal";

interface EditGridModalProps {
  grid: GridConfig;
  onClose: () => void;
}

interface EditBody {
  quantity_per_grid?: number;
  leverage?: number;
  max_hold_minutes: number | null;
  force_exit_minutes: number | null;
  max_deviation_pct: number | null;
  side: string;
  trailing: number;
  atr_multiplier?: number | null;
  ai_enabled?: number;
  ai_auto_act?: number;
}

export default function EditGridModal({ grid, onClose }: EditGridModalProps) {
  const qc = useQueryClient();

  const [qty, setQty] = useState(String(grid.quantity_per_grid));
  const [leverage, setLeverage] = useState(String(grid.leverage));
  const [maxHold, setMaxHold] = useState(grid.max_hold_minutes != null ? String(grid.max_hold_minutes) : "");
  const [forceExit, setForceExit] = useState(grid.force_exit_minutes != null ? String(grid.force_exit_minutes) : "");
  const [maxDev, setMaxDev] = useState(grid.max_deviation_pct != null ? String(grid.max_deviation_pct) : "");
  const [side, setSide] = useState<"long" | "short" | "neutral">(grid.side);
  const [trailing, setTrailing] = useState(!!grid.trailing);
  const [atrMult, setAtrMult] = useState(grid.atr_multiplier != null ? String(grid.atr_multiplier) : "");
  const [aiEnabled, setAiEnabled] = useState(Boolean(grid.ai_enabled));
  const [aiAutoAct, setAiAutoAct] = useState(Boolean(grid.ai_auto_act));
  const [error, setError] = useState("");

  const mutation = useMutation({
    mutationFn: (body: EditBody) => apiPatch(`/grids/${grid.id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grid-detail", grid.id] });
      qc.invalidateQueries({ queryKey: ["grids"] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const body: EditBody = {
      quantity_per_grid: parseFloat(qty) || undefined,
      leverage: parseInt(leverage) || undefined,
      max_hold_minutes: maxHold ? parseInt(maxHold) : null,
      force_exit_minutes: forceExit ? parseInt(forceExit) : null,
      max_deviation_pct: maxDev ? parseFloat(maxDev) : null,
      side,
      trailing: trailing ? 1 : 0,
    };
    if (grid.atr_multiplier != null) {
      body.atr_multiplier = parseFloat(atrMult) || null;
    }
    body.ai_enabled = aiEnabled ? 1 : 0;
    body.ai_auto_act = aiAutoAct ? 1 : 0;
    mutation.mutate(body);
  }

  return (
    <Modal open onClose={onClose} title="Edit Grid">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Qty per Grid">
            <input
              type="number"
              step="any"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className={inputCls}
              required
            />
          </Field>
          <Field label="Leverage">
            <input
              type="number"
              value={leverage}
              onChange={(e) => setLeverage(e.target.value)}
              className={inputCls}
              required
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Break-Even Exit (min)">
            <input
              type="number"
              step="1"
              value={maxHold}
              onChange={(e) => setMaxHold(e.target.value)}
              className={inputCls}
              placeholder="blank = off"
            />
          </Field>
          <Field label="Force Exit (min)">
            <input
              type="number"
              step="1"
              value={forceExit}
              onChange={(e) => setForceExit(e.target.value)}
              className={inputCls}
              placeholder="blank = off"
            />
          </Field>
        </div>

        <Field label="Max Deviation Stop (%)">
          <input
            type="number"
            step="0.01"
            value={maxDev}
            onChange={(e) => setMaxDev(e.target.value)}
            className={inputCls}
            placeholder="e.g. 0.6 = cut at 0.6% adverse move"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Side">
            <select value={side} onChange={(e) => setSide(e.target.value as "long" | "short" | "neutral")} className={inputCls}>
              <option value="neutral">Neutral</option>
              <option value="long">Long</option>
              <option value="short">Short</option>
            </select>
          </Field>
          <Field label=" ">
            <label className="flex items-center gap-2 cursor-pointer mt-2">
              <input
                type="checkbox"
                checked={trailing}
                onChange={(e) => setTrailing(e.target.checked)}
                className="accent-accent"
              />
              <span className="text-sm text-dim">Trailing</span>
            </label>
          </Field>
        </div>

        {grid.atr_multiplier != null && (
          <Field label="ATR Multiplier">
            <input
              type="number"
              step="0.1"
              value={atrMult}
              onChange={(e) => setAtrMult(e.target.value)}
              className={inputCls}
            />
          </Field>
        )}

        {/* AI settings — stacked vertically with descriptions */}
        <div className="space-y-2 pt-2 border-t border-border">
          <label className="flex items-start gap-2 cursor-pointer text-sm text-dim">
            <input
              type="checkbox"
              checked={aiEnabled}
              onChange={(e) => {
                setAiEnabled(e.target.checked);
                if (!e.target.checked) setAiAutoAct(false);
              }}
              className="accent-accent mt-0.5"
            />
            <span className="flex-1">
              <span className="font-medium text-text">Enable AI advisor</span>
              <span className="block text-[0.72rem] text-muted">
                Sends market data to AI; advice appears in UI (advisory only).
              </span>
            </span>
          </label>
          <label
            className={`flex items-start gap-2 text-sm text-dim ${
              aiEnabled ? "cursor-pointer" : "opacity-50 cursor-not-allowed"
            }`}
          >
            <input
              type="checkbox"
              checked={aiAutoAct}
              onChange={(e) => setAiAutoAct(e.target.checked)}
              className="accent-accent mt-0.5"
              disabled={!aiEnabled}
            />
            <span className="flex-1">
              <span className="font-medium text-text">AI auto-act</span>
              <span className="block text-[0.72rem] text-muted">
                AI automatically executes decisions (PAUSE/RESUME/CUT, param tuning).
              </span>
            </span>
          </label>
          {aiAutoAct && aiEnabled && (
            <div className="text-[0.72rem] text-warn bg-warn/10 rounded px-2 py-1.5">
              <span className="opacity-85 mr-1">⚠</span>
              AI will execute decisions automatically — verify conf thresholds before live use.
            </div>
          )}
        </div>

        {error && <div className="text-danger text-xs">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={ghostBtn}>
            Cancel
          </button>
          <button type="submit" disabled={mutation.isPending} className={primaryBtn}>
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-wider text-muted mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full bg-bg border border-border rounded px-2.5 py-1.5 text-sm text-text outline-none focus:border-accent/60 transition-colors";
const primaryBtn =
  "text-xs px-4 py-2 rounded bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 transition-colors disabled:opacity-50";
const ghostBtn =
  "text-xs px-4 py-2 rounded border border-border text-muted hover:text-text transition-colors";
